import React from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import CustomTooltip from "@/components/shared/CustomTooltip";
import Skeleton from "@/components/shared/Skeleton";
import { plural } from "@/utils/formatText";

import { PILL_NEUTRAL, TONE } from "../constants";
import Chip from "./Chip";

/**
 * One drift axis: how many databases carry the code's version of this thing,
 * what that version is, and the single action that closes the gap.
 *
 * `unknown` is separate from `behind` on purpose. A database that could not be
 * probed is neither behind nor up to date, and folding it into either count
 * hides the one state that needs a human.
 *
 * The line under the header is what makes the value readable. `target` alone is
 * a hash or a bare integer — `c4e91a3`, `v16`, `46` — and on its own it says
 * neither what it is nor what is in it. `targetLabel` names the value and
 * `targetName` says what that version contains, so the tile reads
 * "Code head · job experience becomes a range" rather than seven characters of
 * hash.
 *
 * The header block sits under one `CustomTooltip` rather than a native `title`
 * on each half of it — two overlapping native tooltips on adjacent elements
 * used to retrigger as the pointer crossed from one to the other, showing the
 * same string twice. The tooltip hangs off `target` alone, because that is the
 * only part of the header that can be shorter than the fact behind it:
 * `targetLabel` and `targetName` are already on screen in full. Pass
 * `targetTitle` when there is more to say than `target` shows — the full hash
 * behind a 7-character slice, or the summary behind a version number.
 *
 * @param {number} total       databases this axis's action can reach
 * @param {number} behind      reachable databases that are not on the code version
 * @param {number} unknown     databases that could not be probed
 * @param {string} unit        singular noun for `total`, e.g. "dedicated database"
 * @param {string} [targetName] what the code's version is called
 * @param {string} [note]      one extra line of scale, shown under the chips
 */
const FleetTile = ({
    icon: Icon,
    title,
    target,
    targetLabel,
    targetName,
    targetTitle,
    note,
    loading,
    total,
    behind,
    unknown,
    unit,
    actionLabel,
    actionIcon: ActionIcon,
    actionTitle,
    disabledTitle,
    onAction,
    disabled,
}) => {
    const upToDate = Math.max(0, total - behind - unknown);

    return (
        <Card className="p-3 border-border-primary flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-[12px] font-semibold text-text-primary">
                        <Icon size={13} aria-hidden="true" className="text-text-secondary" />
                        {title}
                    </div>
                    {/* Only wrap this in a tooltip when there's something to
                        reveal that isn't already on screen — the displayed
                        value is a 7-char hash slice; `targetTitle`, when
                        passed, is the full hash. A version number like "v16"
                        isn't truncated, so no tooltip: nothing to add. */}
                    {targetTitle ? (
                        <CustomTooltip text={targetTitle}>
                            <div className="text-[11px] text-text-secondary font-mono truncate cursor-default">
                                {target}
                            </div>
                        </CustomTooltip>
                    ) : (
                        <div className="text-[11px] text-text-secondary font-mono truncate">{target}</div>
                    )}
                </div>

                {/* What the value above is, and what is in it — already
                    visible, so it carries no tooltip of its own. */}
                <div className="text-[11px] leading-snug">
                    <span className="text-text-muted">{targetLabel}</span>
                    {loading ? (
                        <Skeleton className="h-3 w-32 mt-0.5" />
                    ) : (
                        targetName && (
                            <>
                                <span className="text-text-muted"> · </span>
                                <span className="text-text-secondary">{targetName}</span>
                            </>
                        )
                    )}
                </div>
            </div>

            {loading ? (
                <Skeleton className="h-7 w-32" />
            ) : (
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[22px] font-semibold leading-none text-text-primary">
                        {upToDate}
                    </span>
                    <span className="text-[12px] text-text-secondary">
                        of {plural(total, unit, `${unit}s`)} up to date
                    </span>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-1.5 min-h-[22px]">
                {loading ? (
                    <Skeleton className="h-4 w-24" />
                ) : (
                    <>
                        {behind > 0 && (
                            <Chip
                                tone={TONE.amber}
                                icon={<AlertCircle size={10} />}
                                label={`${behind} behind`}
                            />
                        )}
                        {unknown > 0 && (
                            <Chip
                                tone={TONE.slate}
                                icon={<AlertCircle size={10} />}
                                label={`${unknown} unknown`}
                            />
                        )}
                        {behind === 0 && unknown === 0 && total > 0 && (
                            <Chip
                                tone={TONE.emerald}
                                icon={<CheckCircle2 size={10} />}
                                label="All up to date"
                            />
                        )}
                    </>
                )}
            </div>

            {/* How big the gap is, where a count of databases does not say it.
                "1 behind" is one database and an unknown number of changes. */}
            {!loading && note && (
                <div className="text-[11px] text-text-secondary leading-snug">
                    {note}
                </div>
            )}

            <Button
                onClick={onAction}
                disabled={disabled}
                title={disabled ? disabledTitle : actionTitle}
                className={`${PILL_NEUTRAL} w-fit`}
            >
                <ActionIcon size={12} aria-hidden="true" />
                {actionLabel}
            </Button>
        </Card>
    );
};

export default FleetTile;